import React, { useMemo } from "react";
import moment from "moment";
import styled from "styled-components";
import { colors2, SkeletonLoader } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION_DF } from "oolib/dist/v2/components/Typo2";
import { ChartLine } from "@phosphor-icons/react";
import { TrendLineChart } from "@/_self_managed/SM_Dashboard/LensesV2/comps/TrendLineChart";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { useAggrMetricsAcrossTimeAndDimension } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetLineChart/query/useAggrMetricsAcrossTimeAndDimension";
import { CONFIG } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/CONFIG";
import { periodGrain } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periodKeys";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { periodFilter } from "./scopeFilters";

/**
 * The signal categories over time, one line each: observations carrying a
 * signal of the category, per period at the period's grain, over the
 * history window (HISTORY_MAX_PERIODS before the period, to its end). Owns
 * its one request — the metrics engine's `signalCategory` dimension over
 * time, so an observation counts once per category it carries, the same
 * reading as the categories widget above. Plotted from the first period
 * with any observations to the end of the window, zero periods in between
 * included (the engine emits every period in the range) — the window
 * itself can reach back before the corpus starts. Sits in the Overview V2
 * widget shell; `blurNote` (and its icon) pass through to it.
 * `singlePeriod` is the section's call that only one period has data — the
 * same flag that sets the note — and switches the lead-in below on.
 *
 *   period, dateFieldMap   the shared period selection's period and the
 *                          per-dataset date that matters
 */
const DIMENSION = { dimensionType: "signalCategory" };
const GRAIN_UNIT = { week: "weeks", month: "months", year: "years" };
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
`;
// Zero periods kept ahead of a lone period with data (singlePeriod).
const LEAD_IN_PERIODS = 6;
const Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`;

/**
 * The trend's data: one request (skipped when `prefetchedRows` replays a
 * saved answer), shaped into the chart's rows and colours. `activeFilters`
 * narrows the observations counted (the report's + a widget's own filters);
 * `rowsByPeriod` is the raw answer, for a caller that saves it.
 * `historyPeriods`: how many periods before the period the window reaches
 * back (the dashboard's full history by default). `trimLeadingEmpty`: drop
 * the empty periods before the first with data — for a window that may reach
 * back past the corpus (the dashboard's); off when the window was picked and
 * an empty period is part of the answer.
 */
export const useSignalCategoryTrendData = ({ period, dateFieldMap, singlePeriod = false, activeFilters, prefetchedRows, historyPeriods = CONFIG.HISTORY_MAX_PERIODS, trimLeadingEmpty = true }) => {
  const categoryTheme = useSignalCategories();
  const grain = periodGrain(period) || "month";
  const timelineRange = useMemo(
    () => (period?.from && period?.to ? { from: moment(period.from).subtract(historyPeriods, GRAIN_UNIT[grain] || "months").toISOString(), to: period.to } : undefined),
    [period, grain, historyPeriods]
  );
  // timelineRange only zero-fills the axis — the engine still counts every
  // period with data — so the window also goes in as the period filter
  // (each dataset read on its date that matters), the way the priorities
  // scorer limits its own counts.
  const filters = useMemo(
    () => [periodFilter(timelineRange && { value: "trend_window", ...timelineRange }), ...(activeFilters || [])].filter(Boolean),
    [timelineRange, activeFilters]
  );
  const { data: fetchedRows, isLoading: fetching } = useAggrMetricsAcrossTimeAndDimension({
    contentType: "annotations",
    dateValuePathByContentType: dateFieldMap,
    timelineRange,
    interval: grain,
    dimensionValuePath: DIMENSION,
    includeNullDimensions: true,
    activeFilters: filters,
    queryOptions: { enabled: !prefetchedRows && !!timelineRange && !!dateFieldMap },
  });
  const rowsByPeriod = prefetchedRows || fetchedRows;
  const isLoading = !prefetchedRows && fetching;
  const data = useMemo(() => {
    // Every period in the window, the categories in the tenant's order.
    const rows = (rowsByPeriod || []).map((row) => ({
      timeInterval: row.timeInterval,
      dimensions: categoryTheme.list.map((cat) => ({
        value: cat.value,
        display: cat.display,
        count: (row.dimensions || []).find((d) => d.value === cat.value)?.count || 0,
      })),
    }));
    const first = rows.findIndex((row) => row.dimensions.some((d) => d.count > 0));
    if (!trimLeadingEmpty || first < 0) return rows;
    // A single period with observations leaves nothing to draw a line
    // between, so keep some of the zero months before it then (not the
    // whole window — a year of empty axis reads as missing data, not as
    // a teaser): the widget sits blurred behind the more-data note and
    // should still show a line rising into its month.
    return rows.slice(singlePeriod ? Math.max(0, first - LEAD_IN_PERIODS) : first);
  }, [rowsByPeriod, categoryTheme, singlePeriod, trimLeadingEmpty]);
  const colors = useMemo(() => Object.fromEntries(categoryTheme.list.map((cat) => [cat.display, categoryTheme.accentOf(cat.value)])), [categoryTheme]);
  return { data, colors, grain, isLoading, rowsByPeriod };
};

/** The chart and its legend — no card around it, so a host brings its own. */
export const SignalCategoryTrendChart = ({ data, colors, grain, isLoading }) => {
  const categoryTheme = useSignalCategories();
  return (
    <>
      {isLoading ? (
        <SkeletonLoader style={{ height: 260, borderRadius: 8 }} />
      ) : (
        <TrendLineChart data={data} colors={colors} interval={grain} height={260} defaultOpacity={1} hideTopGridLine />
      )}
      <Legend style={{ marginTop: 12 }}>
        {categoryTheme.list.map((cat) => (
          <UI_CAPTION_DF key={cat.value} style={{ color: colors2.grey60, whiteSpace: "nowrap" }}>
            <Dot $color={categoryTheme.accentOf(cat.value)} />
            {cat.display}
          </UI_CAPTION_DF>
        ))}
      </Legend>
    </>
  );
};

export const SignalCategoryTrend = ({ period, dateFieldMap, singlePeriod = false, blurNote, blurNoteIcon }) => {
  const trend = useSignalCategoryTrendData({ period, dateFieldMap, singlePeriod });

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <ChartLine size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Signal categories over time</UI_BODY_SEMIBOLD_SM_DF>
        </div>
      }
      style={{ height: "100%" }}
      blurNote={blurNote}
      blurNoteIcon={blurNoteIcon}
    >
      <SignalCategoryTrendChart {...trend} />
    </DashboardWidgetShell>
  );
};
